import type { LanguageCode, LocaleResource } from './types';

const skoletoonsResources: Record<LanguageCode, LocaleResource> = {
  en: {
    common: {
      search: 'Search',
      loadMore: 'See more',
      open: 'Open',
      send: 'Send',
      close: 'Close',
      explore: 'Explore',
      play: 'Play',
      pause: 'Pause',
      paused: 'Paused',
      hide: 'Hide',
    },
    home: {
      collectionsTitle: 'Collections',
      allSee: 'View all',
      shopLabel: 'Shop',
      newProductsTitle: 'New arrivals',
      shopCta: 'View shop',
      newsVideoTitle: "SkoleToon's News",
      newsPageAria: 'View news',
      newsHeroTitle: 'News',
      newsHeroMeta: 'News / Drops / Culture',
      newsArticleCta: 'View article',
      nowPlaying: 'Now playing',
      musiqueDescription: "A track, a vibe. The whole universe of SkoleToon's.",
      musicVisualAlt: "SkoleToon's music visual",
      musicVisualBadge: 'Visual / Photos',
      liveSpectrum: 'Live audio visualization',
      seekTrack: 'Seek track',
      restartTrack: 'Replay',
      downloadTrack: 'Download track',
      presentedBy: 'Skoleom presents',
      downloadExtension: 'Download extension',
      playVideo: 'Play video',
      carouselPrev: 'Previous collection',
      carouselNext: 'Next collection',
      heroSlides: {
        'build-different': { collection: 'Build Different', season: 'New collection' },
        'a-modeste': { collection: 'A.Modeste', season: 'New collection' },
        'on-the-road': { collection: 'On The Road', season: 'Summer edit' },
        'maillots-sports': { collection: 'Bloc 90', season: 'Limited drop' },
        ontario: { collection: 'Ontario', season: 'New collection' },
      },
    },
    stores: {
      badge: 'Shop',
      titleA: 'The',
      titleB: 'Store',
      description:
        'Discover sport outfits and collections: jerseys, tracksuits, accessories and exclusive capsules.',
      aboutEyebrow: 'About',
      aboutLine1:
        "SkoleToon's creates pieces at the intersection of sport, style, and visual culture.",
      aboutLine2:
        'The store brings together essentials, stronger silhouettes, and more exclusive drops in one curated space.',
      seePiece: 'View item',
      products: 'Products',
      loaded: '{{count}} loaded',
      reset: 'Reset',
      emptyTitle: 'Nothing here yet',
      emptyText:
        'No products match your current selection. Explore another collection or check back soon for the next drop.',
      kidsUnavailable: 'Kids products unavailable',
      kidsUnavailableText:
        'These products are not available for kids right now. Explore another category or check back soon.',
      allDisplayed: 'All products are displayed',
      allCollections: 'All collections',
      allCategories: 'All categories',
      filtersAria: 'Product filters',
      collectionsTitle: 'Our collections',
      categoriesTitle: 'Categories',
      searchPlaceholder: 'Search',
      searchAria: 'Search product',
      filterCollectionAria: 'Filter by collection',
      removeFavorite: 'Remove from favorites',
      addFavorite: 'Add to favorites',
      sale: 'Sale',
      soldOut: 'Sold out',
      outOfStock: 'Out of stock',
      adding: 'Adding...',
      added: 'Added!',
      chooseOption: 'Choose option',
      addToCart: 'Add to cart',
      add: 'Add',
      iconic: {
        arBadge: 'AR try-on available',
        iconicBadge: 'Iconic collection',
        titleLine1: 'On the road',
        titleLine2: 'to success',
        brand: "— SkoleToon's",
        description:
          'The signature collection inspired by motorsport and performance. Jerseys, reversible jackets and checker accessories.',
        cta: 'Discover the collection',
      },
      contest: {
        title: 'Win a t-shirt',
        text: 'Just a few more members before the contest launches. At 5000 sign-ups, 50 official jerseys will be up for grabs.',
        cta: 'Join now',
        note: '* Offer valid until August 2026.',
      },
    },
    piece: {
      collection: 'Collection',
      detail: 'Product detail',
      less: 'Less',
      more: 'More',
      readLess: 'Read less',
      readMore: 'Read more',
      price: 'Price',
      inStock: 'In stock',
      outOfStock: 'Out of stock',
      size: 'Size',
      quantity: 'Qty',
      addToCart: 'Add to cart',
      adding: 'Adding...',
      chooseOption: 'Choose an option',
      failedAdd: 'Failed to add to cart',
      saved: 'Saved',
      save: 'Save to favourites',
      serviceUnavailable: 'Service unavailable',
      serviceUnavailableText: 'This service is not available in your region.',
      back: 'Back',
      previous: 'Previous',
      next: 'Next',
    },
    music: {
      pageTitleA: 'One track,',
      pageTitleB: 'one universe',
      pageDescription: "They See Us Now is not just a song. It's the energy of a movement.",
      comingSoonKicker: 'Skoleom Music',
      comingSoonTitle: 'Coming soon',
      comingSoonDescription:
        'The music page is coming soon. We are still polishing the experience before release.',
    },
    news: {
      badge: 'Skoletoon x Skoleom Newsroom',
      titleA: 'News',
      titleB: "SkoleToon's & Skoleom",
      description:
        'All official announcements about product progress, partnerships and ecosystem rollouts across Skoletoon and Skoleom.',
      featured: 'Featured',
      readArticle: 'Read article',
      stayInformed: 'Stay informed',
      subscribe: 'Subscribe',
      nextAnnouncement: 'Next announcement',
      shopCta: 'View shop',
      newsCta: 'View news',
      worldNews: 'World news',
      todayHighlights: "Today's highlights, right at your fingertips",
      lifestyle: 'Lifestyle',
      newsletterBody:
        'Get Skoleom updates, upcoming product releases and partnership announcements.',
      openFormCta: 'Click here to subscribe',
      validating: 'Checking...',
      form: {
        civility: 'Civility',
        mr: 'Mr',
        mrs: 'Mrs',
        mx: 'Mx',
        firstName: 'Your first name',
        lastName: 'Your last name',
        birthDate: 'Date of birth',
        address: 'Your address',
        city: 'Your city',
        postalCode: 'Postal code',
        email: 'Your email',
        consent: 'I agree to receive product updates and announcements by email.',
      },
      messages: {
        invalidEmail: 'Enter a valid email address to subscribe to announcements.',
        invalidName: 'Enter your first name to complete the subscription.',
        invalidLastName: 'Enter your last name to complete the subscription.',
        invalidCivility: 'Select your civility before subscribing.',
        invalidBirthDate: 'Enter your birth date to complete the subscription.',
        invalidStreetAddress: 'Enter your address to complete the subscription.',
        invalidCity: 'Enter your city to complete the subscription.',
        invalidPostalCode: 'Enter your postal code to complete the subscription.',
        missingConsent: 'Please accept updates consent before subscribing.',
        subscribed: 'Subscription confirmed. You will receive upcoming announcements.',
        alreadySubscribed: 'This email is already subscribed.',
        subscribeError: 'Subscription is unavailable right now. Please try again shortly.',
      },
    },
    search: {
      item: 'Item',
      collection: 'Collection',
      shop: 'Shop',
      brand: 'Brand',
      category: 'Category',
      dialog: 'Search',
      placeholder: 'Search item, brand, category...',
      clear: 'Clear',
      noResultsFor: 'No results for',
      featured: 'Featured',
      searchingShop: 'Searching shop...',
      seeAllResults: 'See all results for "{{query}}"',
    },
  },
  fr: {
    common: {
      search: 'Rechercher',
      loadMore: 'Voir plus',
      open: 'Ouvrir',
      send: 'Envoyer',
      close: 'Fermer',
      explore: 'Explorer',
      play: 'Lire',
      pause: 'Pause',
      paused: 'En pause',
      hide: 'Masquer',
    },
    home: {
      collectionsTitle: 'Collections',
      allSee: 'Tout voir',
      shopLabel: 'Boutique',
      newProductsTitle: 'Nouveautés',
      shopCta: 'Voir la boutique',
      newsVideoTitle: "Actualités SkoleToon's",
      newsPageAria: 'Voir les actualités',
      newsHeroTitle: 'Actualités',
      newsHeroMeta: 'News / Drops / Culture',
      newsArticleCta: "Voir l'article",
      nowPlaying: 'En cours de lecture',
      musiqueDescription: "Un son, une vibe. Tout l'univers de SkoleToon's.",
      musicVisualAlt: "Visuel musique SkoleToon's",
      musicVisualBadge: 'Visuel / Photos',
      liveSpectrum: 'Visualisation audio en temps réel',
      seekTrack: 'Naviguer dans le son',
      restartTrack: 'Rejouer',
      downloadTrack: 'Télécharger le son',
      presentedBy: 'Skoleom présente',
      downloadExtension: "Télécharger l'extension",
      playVideo: 'Lire la vidéo',
      carouselPrev: 'Collection précédente',
      carouselNext: 'Collection suivante',
      heroSlides: {
        'build-different': { collection: 'Build Different', season: 'Nouvelle collection' },
        'a-modeste': { collection: 'A.Modeste', season: 'Nouvelle collection' },
        'on-the-road': { collection: 'On The Road', season: "Édition d'été" },
        'maillots-sports': { collection: 'Bloc 90', season: 'Drop limité' },
        ontario: { collection: 'Ontario', season: 'Nouvelle collection' },
      },
    },
    stores: {
      badge: 'Boutique',
      titleA: 'La',
      titleB: 'Boutique',
      description:
        'Explorez une sélection de silhouettes sport et lifestyle : pièces Bloc 90, survêtements, accessoires et capsules exclusives.',
      aboutEyebrow: 'À propos',
      aboutLine1:
        'SkoleToon’s crée des pièces à la croisée du sport, du style et de la culture visuelle.',
      aboutLine2:
        'La boutique réunit des essentiels, des coupes affirmées et des drops exclusifs dans un même espace.',
      seePiece: 'Voir la pièce',
      products: 'Produits',
      loaded: '{{count}} chargé(s)',
      reset: 'Réinitialiser',
      emptyTitle: 'Aucun produit pour le moment',
      emptyText:
        'Aucun produit ne correspond à votre sélection. Explorez une autre collection ou revenez bientôt.',
      kidsUnavailable: 'Produits enfant indisponibles',
      kidsUnavailableText:
        'Ces produits ne sont pas disponibles pour les enfants pour le moment. Explorez une autre catégorie ou revenez bientôt.',
      allDisplayed: 'Tous les produits sont affichés',
      allCollections: 'Toutes les collections',
      allCategories: 'Toutes les catégories',
      filtersAria: 'Filtres produits',
      collectionsTitle: 'Nos collections',
      categoriesTitle: 'Catégories',
      searchPlaceholder: 'Rechercher',
      searchAria: 'Rechercher un produit',
      filterCollectionAria: 'Filtrer par collection',
      removeFavorite: 'Retirer des favoris',
      addFavorite: 'Ajouter aux favoris',
      sale: 'Promo',
      soldOut: 'Épuisé',
      outOfStock: 'Rupture',
      adding: 'Ajout...',
      added: 'Ajouté !',
      chooseOption: 'Choisir une option',
      addToCart: 'Ajouter au panier',
      add: 'Ajouter',
      iconic: {
        arBadge: 'Essayage AR disponible',
        iconicBadge: 'Collection iconique',
        titleLine1: 'On the road',
        titleLine2: 'to success',
        brand: "— SkoleToon's",
        description:
          'La collection signature inspirée du sport automobile et de la performance. Pièces Bloc 90, vestes reverso et accessoires checker.',
        cta: 'Découvrir la collection',
      },
      contest: {
        title: 'Tentez de gagner un t-shirt',
        text: 'Plus que quelques membres avant le lancement du concours. À 5000 inscrits, 50 maillots officiels seront mis en jeu.',
        cta: 'Participer maintenant',
        note: "* Offre valable jusqu'à août 2026.",
      },
    },
    piece: {
      collection: 'Collection',
      detail: 'Détail produit',
      less: 'Moins',
      more: 'Plus',
      readLess: 'Lire moins',
      readMore: 'Lire plus',
      price: 'Prix',
      inStock: 'En stock',
      outOfStock: 'Rupture',
      size: 'Taille',
      quantity: 'Qté',
      addToCart: 'Ajouter au panier',
      adding: 'Ajout...',
      chooseOption: 'Choisir une option',
      failedAdd: "Échec de l'ajout au panier",
      saved: 'Sauvegardé',
      save: 'Ajouter aux favoris',
      serviceUnavailable: 'Service non disponible',
      serviceUnavailableText: "Ce service n'est pas disponible dans votre région.",
      back: 'Retour',
      previous: 'Précédent',
      next: 'Suivant',
    },
    music: {
      pageTitleA: 'Un seul son,',
      pageTitleB: 'un univers',
      pageDescription: "They See Us Now n'est pas juste un son. C'est l'énergie d'un mouvement.",
      comingSoonKicker: 'Skoleom Music',
      comingSoonTitle: 'À venir',
      comingSoonDescription:
        "La page musique arrive bientôt. On finalise encore l'expérience avant de la rendre disponible.",
    },
    news: {
      badge: 'Salle de presse Skoletoon x Skoleom',
      titleA: 'Actualités',
      titleB: "SkoleToon's & Skoleom",
      description:
        "Toutes les annonces officielles autour des avancées produit, partenariats et déploiements de l'écosystème Skoletoon et Skoleom.",
      featured: 'À la une',
      readArticle: "Lire l'article",
      stayInformed: 'Reste informé',
      subscribe: "S'abonner",
      nextAnnouncement: 'Prochaine annonce',
      shopCta: 'Voir la boutique',
      newsCta: "Voir l'actualité",
      worldNews: 'Actualités mondiales',
      todayHighlights: 'Les nouveautés du jour, à portée de main',
      lifestyle: 'Mode',
      newsletterBody:
        'Reçois les nouveautés Skoleom, les prochaines sorties produits et les annonces de partenariats.',
      openFormCta: "Cliquer ici pour s'inscrire",
      validating: 'Validation...',
      form: {
        civility: 'Civilité',
        mr: 'M.',
        mrs: 'Mme',
        mx: 'Autre',
        firstName: 'Ton prénom',
        lastName: 'Ton nom',
        birthDate: 'Date de naissance',
        address: 'Ton adresse',
        city: 'Ta ville',
        postalCode: 'Code postal',
        email: 'Ton email',
        consent: "J'accepte de recevoir par email les annonces et nouveautés produit.",
      },
      messages: {
        invalidEmail: "Entre une adresse email valide pour t'abonner aux annonces.",
        invalidName: "Entre ton prénom pour finaliser l'inscription.",
        invalidLastName: "Entre ton nom pour finaliser l'inscription.",
        invalidCivility: "Sélectionne ta civilité avant de t'inscrire.",
        invalidBirthDate: 'Entre ta date de naissance pour finaliser l’inscription.',
        invalidStreetAddress: "Entre ton adresse pour finaliser l'inscription.",
        invalidCity: "Entre ta ville pour finaliser l'inscription.",
        invalidPostalCode: "Entre ton code postal pour finaliser l'inscription.",
        missingConsent: "Accepte le consentement avant de t'inscrire.",
        subscribed: 'Inscription validée. Tu recevras les prochaines annonces.',
        alreadySubscribed: 'Cet email est déjà inscrit.',
        subscribeError: 'Inscription impossible pour le moment. Réessaie dans un instant.',
      },
    },
    search: {
      item: 'Pièce',
      collection: 'Collection',
      shop: 'Boutique',
      brand: 'Marque',
      category: 'Catégorie',
      dialog: 'Recherche',
      placeholder: 'Rechercher pièce, marque, catégorie...',
      clear: 'Effacer',
      noResultsFor: 'Aucun résultat pour',
      featured: 'Tendances',
      searchingShop: 'Recherche boutique...',
      seeAllResults: 'Voir tous les résultats pour « {{query}} »',
    },
  },
  es: {
    common: {
      search: 'Buscar',
      loadMore: 'Ver más',
      open: 'Abrir',
      send: 'Enviar',
      close: 'Cerrar',
      explore: 'Explorar',
      play: 'Reproducir',
      pause: 'Pausa',
      paused: 'En pausa',
      hide: 'Ocultar',
    },
    home: {
      collectionsTitle: 'Colecciones',
      allSee: 'Ver todo',
      shopLabel: 'Tienda',
      newProductsTitle: 'Novedades',
      shopCta: 'Ver la tienda',
      newsVideoTitle: "Noticias de SkoleToon's",
      newsPageAria: 'Ver noticias',
      newsHeroTitle: 'Noticias',
      newsHeroMeta: 'Noticias / Drops / Cultura',
      newsArticleCta: 'Ver artículo',
      nowPlaying: 'Reproduciendo',
      musiqueDescription: "Un tema, una vibra. Todo el universo de SkoleToon's.",
      musicVisualAlt: "Visual musical de SkoleToon's",
      musicVisualBadge: 'Visual / Fotos',
      liveSpectrum: 'Visualización de audio en tiempo real',
      seekTrack: 'Buscar en la pista',
      restartTrack: 'Repetir',
      downloadTrack: 'Descargar pista',
      presentedBy: 'Skoleom presenta',
      downloadExtension: 'Descargar la extensión',
      playVideo: 'Ver vídeo',
      carouselPrev: 'Colección anterior',
      carouselNext: 'Colección siguiente',
      heroSlides: {
        'build-different': { collection: 'Build Different', season: 'Nueva colección' },
        'a-modeste': { collection: 'A.Modeste', season: 'Nueva colección' },
        'on-the-road': { collection: 'On The Road', season: 'Edición de verano' },
        'maillots-sports': { collection: 'Bloc 90', season: 'Drop limitado' },
        ontario: { collection: 'Ontario', season: 'Nueva colección' },
      },
    },
    stores: {
      badge: 'Tienda',
      titleA: 'La',
      titleB: 'Tienda',
      description:
        'Descubre looks y colecciones deportivas: camisetas, chándales, accesorios y cápsulas exclusivas.',
      aboutEyebrow: 'Acerca de',
      aboutLine1: 'SkoleToon’s crea piezas en el cruce entre deporte, estilo y cultura visual.',
      aboutLine2:
        'La tienda reúne esenciales, siluetas con carácter y drops exclusivos en un solo espacio curado.',
      seePiece: 'Ver pieza',
      products: 'Productos',
      loaded: '{{count}} cargado(s)',
      reset: 'Restablecer',
      emptyTitle: 'Nada por aquí todavía',
      emptyText:
        'Ningún producto coincide con tu selección. Explora otra colección o vuelve pronto.',
      kidsUnavailable: 'Productos infantiles no disponibles',
      kidsUnavailableText:
        'Estos productos no están disponibles para niños por ahora. Explora otra categoría o vuelve pronto.',
      allDisplayed: 'Todos los productos están visibles',
      allCollections: 'Todas las colecciones',
      allCategories: 'Todas las categorías',
      filtersAria: 'Filtros de productos',
      collectionsTitle: 'Nuestras colecciones',
      categoriesTitle: 'Categorías',
      searchPlaceholder: 'Buscar',
      searchAria: 'Buscar producto',
      filterCollectionAria: 'Filtrar por colección',
      removeFavorite: 'Quitar de favoritos',
      addFavorite: 'Añadir a favoritos',
      sale: 'Oferta',
      soldOut: 'Agotado',
      outOfStock: 'Sin stock',
      adding: 'Añadiendo...',
      added: 'Añadido',
      chooseOption: 'Elegir opción',
      addToCart: 'Añadir al carrito',
      add: 'Añadir',
      iconic: {
        arBadge: 'Prueba AR disponible',
        iconicBadge: 'Colección icónica',
        titleLine1: 'On the road',
        titleLine2: 'to success',
        brand: "— SkoleToon's",
        description:
          'La colección insignia inspirada en el automovilismo y el rendimiento. Camisetas, chaquetas reversibles y accesorios checker.',
        cta: 'Descubrir la colección',
      },
      contest: {
        title: 'Gana una camiseta',
        text: 'Faltan pocos miembros para lanzar el concurso. Con 5000 inscritos, se sortearán 50 camisetas oficiales.',
        cta: 'Participar ahora',
        note: '* Oferta válida hasta agosto de 2026.',
      },
    },
    piece: {
      collection: 'Colección',
      detail: 'Detalle del producto',
      less: 'Menos',
      more: 'Más',
      readLess: 'Leer menos',
      readMore: 'Leer más',
      price: 'Precio',
      inStock: 'En stock',
      outOfStock: 'Sin stock',
      size: 'Talla',
      quantity: 'Cant.',
      addToCart: 'Añadir al carrito',
      adding: 'Añadiendo...',
      chooseOption: 'Elegir opción',
      failedAdd: 'No se pudo añadir al carrito',
      saved: 'Guardado',
      save: 'Guardar en favoritos',
      serviceUnavailable: 'Servicio no disponible',
      serviceUnavailableText: 'Este servicio no está disponible en tu región.',
      back: 'Volver',
      previous: 'Anterior',
      next: 'Siguiente',
    },
    music: {
      pageTitleA: 'Un tema,',
      pageTitleB: 'un universo',
      pageDescription: 'They See Us Now no es solo una canción. Es la energía de un movimiento.',
      comingSoonKicker: 'Skoleom Music',
      comingSoonTitle: 'Próximamente',
      comingSoonDescription:
        'La página de música llega pronto. Estamos puliendo la experiencia antes del lanzamiento.',
    },
    news: {
      badge: 'Sala de prensa Skoletoon x Skoleom',
      titleA: 'Noticias',
      titleB: "SkoleToon's & Skoleom",
      description:
        'Todas las noticias oficiales sobre producto, colaboraciones y despliegues del ecosistema Skoletoon y Skoleom.',
      featured: 'Destacado',
      readArticle: 'Leer artículo',
      stayInformed: 'Mantente informado',
      subscribe: 'Suscribirse',
      nextAnnouncement: 'Próximo anuncio',
      shopCta: 'Ver la tienda',
      newsCta: 'Ver noticias',
      worldNews: 'Noticias globales',
      todayHighlights: 'Las novedades de hoy, al alcance de tu mano',
      lifestyle: 'Moda',
      newsletterBody:
        'Recibe novedades de Skoleom, próximos lanzamientos de producto y anuncios de colaboración.',
      openFormCta: 'Haz clic aquí para suscribirte',
      validating: 'Comprobando...',
      form: {
        civility: 'Tratamiento',
        mr: 'Sr.',
        mrs: 'Sra.',
        mx: 'Mx',
        firstName: 'Tu nombre',
        lastName: 'Tus apellidos',
        birthDate: 'Fecha de nacimiento',
        address: 'Tu dirección',
        city: 'Tu ciudad',
        postalCode: 'Código postal',
        email: 'Tu email',
        consent: 'Acepto recibir novedades y anuncios de producto por email.',
      },
      messages: {
        invalidEmail: 'Introduce un email válido para suscribirte.',
        invalidName: 'Introduce tu nombre para completar la suscripción.',
        invalidLastName: 'Introduce tus apellidos para completar la suscripción.',
        invalidCivility: 'Selecciona tu tratamiento antes de suscribirte.',
        invalidBirthDate: 'Introduce tu fecha de nacimiento.',
        invalidStreetAddress: 'Introduce tu dirección.',
        invalidCity: 'Introduce tu ciudad.',
        invalidPostalCode: 'Introduce tu código postal.',
        missingConsent: 'Acepta el consentimiento antes de suscribirte.',
        subscribed: 'Suscripción confirmada. Recibirás los próximos anuncios.',
        alreadySubscribed: 'Este email ya está suscrito.',
        subscribeError: 'La suscripción no está disponible ahora. Inténtalo más tarde.',
      },
    },
    search: {
      item: 'Pieza',
      collection: 'Colección',
      shop: 'Tienda',
      brand: 'Marca',
      category: 'Categoría',
      dialog: 'Buscar',
      placeholder: 'Buscar pieza, marca, categoría...',
      clear: 'Borrar',
      noResultsFor: 'Sin resultados para',
      featured: 'Destacados',
      searchingShop: 'Buscando en tienda...',
      seeAllResults: 'Ver todos los resultados de "{{query}}"',
    },
  },
  ar: {},
  pt: {},
  hi: {},
  zh: {},
  id: {},
  ru: {},
  sw: {},
};

const languageCopies: Partial<Record<Exclude<LanguageCode, 'en' | 'fr' | 'es'>, LocaleResource>> = {
  ar: {
    common: {
      search: 'بحث',
      play: 'تشغيل',
      pause: 'إيقاف مؤقت',
      paused: 'متوقف مؤقتا',
    },
    home: {
      collectionsTitle: 'المجموعات',
      allSee: 'عرض الكل',
      shopLabel: 'المتجر',
      newProductsTitle: 'وصل حديثا',
      shopCta: 'عرض المتجر',
      newsVideoTitle: 'أخبار SkoleToon’s',
      newsPageAria: 'عرض الأخبار',
      newsHeroTitle: 'الأخبار',
      newsHeroMeta: 'أخبار / إصدارات / ثقافة',
      newsArticleCta: 'عرض المقال',
      nowPlaying: 'يتم التشغيل الآن',
      musiqueDescription: 'مسار واحد وأجواء واحدة. عالم SkoleToon’s بالكامل.',
      musicVisualAlt: 'الصورة الموسيقية لـ SkoleToon’s',
      musicVisualBadge: 'صور / مرئيات',
      liveSpectrum: 'تصور صوتي مباشر',
      seekTrack: 'التنقل في المسار',
      restartTrack: 'إعادة التشغيل',
      downloadTrack: 'تنزيل المسار',
      presentedBy: 'Skoleom تقدم',
      downloadExtension: 'تنزيل الإضافة',
      playVideo: 'تشغيل الفيديو',
      carouselPrev: 'المجموعة السابقة',
      carouselNext: 'المجموعة التالية',
      heroSlides: {
        'build-different': { collection: 'Build Different', season: 'مجموعة جديدة' },
        'a-modeste': { collection: 'A.Modeste', season: 'مجموعة جديدة' },
        'on-the-road': { collection: 'On The Road', season: 'إصدار الصيف' },
        'maillots-sports': { collection: 'Bloc 90', season: 'إصدار محدود' },
        ontario: { collection: 'Ontario', season: 'مجموعة جديدة' },
      },
    },
    stores: {
      badge: 'متجر',
      titleA: 'الـ',
      titleB: 'متجر',
      featured: 'الأبرز',
      description: 'اكتشف أزياء ومجموعات رياضية: قمصان، بدلات، إكسسوارات وكبسولات حصرية.',
      aboutEyebrow: 'حول',
      aboutLine1: 'تبتكر SkoleToon’s قطعا عند تقاطع الرياضة والأسلوب والثقافة البصرية.',
      aboutLine2: 'يجمع المتجر الأساسيات والقصات القوية والدروب الحصرية في مساحة واحدة منسقة.',
      seePiece: 'عرض القطعة',
      products: 'المنتجات',
      loaded: 'تم تحميل {{count}}',
      reset: 'إعادة ضبط',
      emptyTitle: 'لا يوجد شيء هنا بعد',
      emptyText: 'لا توجد منتجات تطابق اختيارك. جرّب مجموعة أخرى أو عد قريبا.',
      kidsUnavailable: 'منتجات الأطفال غير متاحة',
      kidsUnavailableText: 'هذه المنتجات غير متاحة للأطفال حاليا. جرّب فئة أخرى أو عد قريبا.',
      allDisplayed: 'تم عرض كل المنتجات',
      allCollections: 'كل المجموعات',
      allCategories: 'كل الفئات',
      filtersAria: 'فلاتر المنتجات',
      collectionsTitle: 'مجموعاتنا',
      categoriesTitle: 'الفئات',
      searchPlaceholder: 'بحث',
      searchAria: 'بحث عن منتج',
      filterCollectionAria: 'تصفية حسب المجموعة',
      removeFavorite: 'إزالة من المفضلة',
      addFavorite: 'إضافة إلى المفضلة',
      sale: 'تخفيض',
      soldOut: 'نفد',
      outOfStock: 'غير متوفر',
      adding: 'جار الإضافة...',
      added: 'تمت الإضافة',
      chooseOption: 'اختر خيارا',
      addToCart: 'إضافة إلى السلة',
      add: 'إضافة',
      iconic: {
        arBadge: 'تجربة AR متاحة',
        iconicBadge: 'مجموعة أيقونية',
        titleLine1: 'On the road',
        titleLine2: 'to success',
        brand: "— SkoleToon's",
        description:
          'المجموعة المميزة المستوحاة من رياضة السيارات والأداء. قمصان، سترات قابلة للعكس وإكسسوارات checker.',
        cta: 'اكتشف المجموعة',
      },
    },
    piece: {
      collection: 'المجموعة',
      detail: 'تفاصيل المنتج',
      less: 'أقل',
      more: 'المزيد',
      readLess: 'قراءة أقل',
      readMore: 'قراءة المزيد',
      price: 'السعر',
      inStock: 'متوفر',
      outOfStock: 'غير متوفر',
      size: 'المقاس',
      quantity: 'الكمية',
      addToCart: 'إضافة إلى السلة',
      adding: 'جار الإضافة...',
      chooseOption: 'اختر خيارا',
      failedAdd: 'تعذرت الإضافة إلى السلة',
      saved: 'محفوظ',
      save: 'حفظ في المفضلة',
      serviceUnavailable: 'الخدمة غير متاحة',
      serviceUnavailableText: 'هذه الخدمة غير متاحة في منطقتك.',
      back: 'رجوع',
      previous: 'السابق',
      next: 'التالي',
    },
    music: {
      pageTitleA: 'مسار واحد،',
      pageTitleB: 'عالم واحد',
      pageDescription: 'They See Us Now ليست مجرد أغنية. إنها طاقة حركة كاملة.',
      comingSoonKicker: 'Skoleom Music',
      comingSoonTitle: 'قريبًا',
      comingSoonDescription:
        'صفحة الموسيقى ستتوفر قريبًا. نُنهي اللمسات الأخيرة على التجربة قبل الإطلاق.',
    },
    news: {
      badge: 'غرفة أخبار Skoletoon x Skoleom',
      titleA: 'الأخبار',
      titleB: "SkoleToon's و Skoleom",
      description: 'كل الإعلانات الرسمية حول المنتج والشراكات وتوسع منظومة Skoletoon وSkoleom.',
      featured: 'مميز',
      readArticle: 'قراءة المقال',
      stayInformed: 'ابق على اطلاع',
      subscribe: 'اشترك',
      nextAnnouncement: 'الإعلان القادم',
      shopCta: 'عرض المتجر',
      newsCta: 'عرض الأخبار',
      worldNews: 'أخبار عالمية',
      todayHighlights: 'أبرز أخبار اليوم بين يديك',
      lifestyle: 'أسلوب حياة',
      newsletterBody: 'احصل على تحديثات Skoleom وإصدارات المنتجات وإعلانات الشراكات.',
      openFormCta: 'انقر هنا للاشتراك',
      validating: 'جار التحقق...',
    },
    search: {
      item: 'قطعة',
      collection: 'مجموعة',
      shop: 'متجر',
      brand: 'علامة',
      category: 'فئة',
      dialog: 'بحث',
      placeholder: 'ابحث عن قطعة أو علامة أو فئة...',
      clear: 'مسح',
      noResultsFor: 'لا نتائج لـ',
      featured: 'مختارات',
      searchingShop: 'جار البحث في المتجر...',
      seeAllResults: 'عرض كل النتائج لـ "{{query}}"',
    },
  },
  pt: {
    common: {
      search: 'Pesquisar',
      loadMore: 'Ver mais',
      open: 'Abrir',
      send: 'Enviar',
      close: 'Fechar',
      explore: 'Explorar',
      play: 'Reproduzir',
      pause: 'Pausar',
      paused: 'Pausado',
      hide: 'Ocultar',
    },
    home: {
      collectionsTitle: 'Coleções',
      allSee: 'Ver tudo',
      shopLabel: 'Loja',
      newProductsTitle: 'Novidades',
      shopCta: 'Ver a loja',
      newsVideoTitle: "Notícias SkoleToon's",
      newsPageAria: 'Ver notícias',
      newsHeroTitle: 'Notícias',
      newsHeroMeta: 'Notícias / Drops / Cultura',
      newsArticleCta: 'Ver artigo',
      nowPlaying: 'Tocando agora',
      musiqueDescription: "Uma faixa, uma vibe. Todo o universo SkoleToon's.",
      musicVisualAlt: "Visual musical SkoleToon's",
      musicVisualBadge: 'Visual / Fotos',
      liveSpectrum: 'Visualização de áudio em tempo real',
      seekTrack: 'Navegar na faixa',
      restartTrack: 'Repetir',
      downloadTrack: 'Baixar faixa',
      presentedBy: 'Skoleom apresenta',
      downloadExtension: 'Baixar a extensão',
      playVideo: 'Ver vídeo',
      carouselPrev: 'Coleção anterior',
      carouselNext: 'Próxima coleção',
      heroSlides: {
        'build-different': { collection: 'Build Different', season: 'Nova coleção' },
        'a-modeste': { collection: 'A.Modeste', season: 'Nova coleção' },
        'on-the-road': { collection: 'On The Road', season: 'Edição de verão' },
        'maillots-sports': { collection: 'Bloc 90', season: 'Drop limitado' },
        ontario: { collection: 'Ontario', season: 'Nova coleção' },
      },
    },
    stores: {
      badge: 'Loja',
      titleA: 'A',
      titleB: 'Loja',
      featured: 'Em destaque',
      description:
        'Descubra looks e coleções esportivas: camisetas, agasalhos, acessórios e cápsulas exclusivas.',
      aboutEyebrow: 'Sobre',
      aboutLine1: 'A SkoleToon’s cria peças na interseção entre esporte, estilo e cultura visual.',
      aboutLine2:
        'A loja reúne essenciais, silhuetas marcantes e drops exclusivos em um único espaço curado.',
      seePiece: 'Ver peça',
      products: 'Produtos',
      loaded: '{{count}} carregado(s)',
      reset: 'Redefinir',
      emptyTitle: 'Nada por aqui ainda',
      emptyText:
        'Nenhum produto corresponde à sua seleção. Explore outra coleção ou volte em breve.',
      kidsUnavailable: 'Produtos infantis indisponíveis',
      kidsUnavailableText:
        'Esses produtos não estão disponíveis para crianças no momento. Explore outra categoria ou volte em breve.',
      allDisplayed: 'Todos os produtos estão visíveis',
      allCollections: 'Todas as coleções',
      allCategories: 'Todas as categorias',
      filtersAria: 'Filtros de produtos',
      collectionsTitle: 'Nossas coleções',
      categoriesTitle: 'Categorias',
      searchPlaceholder: 'Pesquisar',
      searchAria: 'Pesquisar produto',
      filterCollectionAria: 'Filtrar por coleção',
      removeFavorite: 'Remover dos favoritos',
      addFavorite: 'Adicionar aos favoritos',
      sale: 'Promoção',
      soldOut: 'Esgotado',
      outOfStock: 'Sem estoque',
      adding: 'Adicionando...',
      added: 'Adicionado',
      chooseOption: 'Escolher opção',
      addToCart: 'Adicionar ao carrinho',
      add: 'Adicionar',
      iconic: {
        arBadge: 'Prova AR disponível',
        iconicBadge: 'Coleção icônica',
        titleLine1: 'On the road',
        titleLine2: 'to success',
        brand: "— SkoleToon's",
        description:
          'A coleção assinatura inspirada no automobilismo e na performance. Jerseys, jaquetas reversíveis e acessórios checker.',
        cta: 'Descobrir a coleção',
      },
    },
    piece: {
      collection: 'Coleção',
      detail: 'Detalhe do produto',
      less: 'Menos',
      more: 'Mais',
      readLess: 'Ler menos',
      readMore: 'Ler mais',
      price: 'Preço',
      inStock: 'Em estoque',
      outOfStock: 'Sem estoque',
      size: 'Tamanho',
      quantity: 'Qtd.',
      addToCart: 'Adicionar ao carrinho',
      adding: 'Adicionando...',
      chooseOption: 'Escolher opção',
      failedAdd: 'Falha ao adicionar ao carrinho',
      saved: 'Salvo',
      save: 'Salvar nos favoritos',
      serviceUnavailable: 'Serviço indisponível',
      serviceUnavailableText: 'Este serviço não está disponível na sua região.',
      back: 'Voltar',
      previous: 'Anterior',
      next: 'Próximo',
    },
    music: {
      pageTitleA: 'Uma faixa,',
      pageTitleB: 'um universo',
      pageDescription: 'They See Us Now não é apenas uma música. É a energia de um movimento.',
      comingSoonKicker: 'Skoleom Music',
      comingSoonTitle: 'Em breve',
      comingSoonDescription:
        'A página de música chega em breve. Estamos finalizando a experiência antes do lançamento.',
    },
    news: {
      badge: 'Redação Skoletoon x Skoleom',
      titleA: 'Notícias',
      titleB: "SkoleToon's & Skoleom",
      description:
        'Todos os anúncios oficiais sobre produto, parcerias e lançamentos do ecossistema Skoletoon e Skoleom.',
      featured: 'Destaque',
      readArticle: 'Ler artigo',
      stayInformed: 'Fique informado',
      subscribe: 'Assinar',
      nextAnnouncement: 'Próximo anúncio',
      shopCta: 'Ver loja',
      newsCta: 'Ver notícias',
      worldNews: 'Notícias globais',
      todayHighlights: 'Destaques de hoje ao seu alcance',
      lifestyle: 'Moda',
      newsletterBody: 'Receba novidades da Skoleom, próximos lançamentos e anúncios de parceria.',
      openFormCta: 'Clique aqui para assinar',
      validating: 'Verificando...',
    },
    search: {
      item: 'Peça',
      collection: 'Coleção',
      shop: 'Loja',
      brand: 'Marca',
      category: 'Categoria',
      dialog: 'Pesquisar',
      placeholder: 'Pesquisar peça, marca, categoria...',
      clear: 'Limpar',
      noResultsFor: 'Sem resultados para',
      featured: 'Destaques',
      searchingShop: 'Pesquisando loja...',
      seeAllResults: 'Ver todos os resultados para "{{query}}"',
    },
  },
  hi: {
    common: { search: 'खोजें', play: 'चलाएं', pause: 'रोकें', paused: 'रुका हुआ' },
    home: {
      collectionsTitle: 'कलेक्शन',
      allSee: 'सब देखें',
      shopLabel: 'दुकान',
      newProductsTitle: 'नए उत्पाद',
      shopCta: 'दुकान देखें',
      newsVideoTitle: "SkoleToon's समाचार",
      newsPageAria: 'समाचार देखें',
      newsHeroTitle: 'समाचार',
      newsHeroMeta: 'समाचार / ड्रॉप / संस्कृति',
      newsArticleCta: 'लेख देखें',
      nowPlaying: 'अभी चल रहा है',
      musiqueDescription: "एक ट्रैक, एक वाइब। पूरा SkoleToon's.",
      musicVisualAlt: "SkoleToon's संगीत विजुअल",
      musicVisualBadge: 'विजुअल / फोटो',
      liveSpectrum: 'लाइव ऑडियो विजुअलाइजेशन',
      seekTrack: 'ट्रैक में जाएं',
      restartTrack: 'फिर चलाएं',
      downloadTrack: 'ट्रैक डाउनलोड करें',
      presentedBy: 'Skoleom प्रस्तुत करता है',
      downloadExtension: 'एक्सटेंशन डाउनलोड करें',
      playVideo: 'वीडियो चलाएं',
      carouselPrev: 'पिछला कलेक्शन',
      carouselNext: 'अगला कलेक्शन',
      heroSlides: {
        'build-different': { collection: 'Build Different', season: 'नया कलेक्शन' },
        'a-modeste': { collection: 'A.Modeste', season: 'नया कलेक्शन' },
        'on-the-road': { collection: 'On The Road', season: 'समर एडिट' },
        'maillots-sports': { collection: 'Bloc 90', season: 'लिमिटेड ड्रॉप' },
        ontario: { collection: 'Ontario', season: 'नया कलेक्शन' },
      },
    },
    stores: {
      badge: 'दुकान',
      titleA: 'द',
      titleB: 'स्टोर',
      featured: 'मुख्य चयन',
      description:
        'स्पोर्ट आउटफिट और कलेक्शन खोजें: जर्सी, ट्रैकसूट, एक्सेसरी और एक्सक्लूसिव कैप्सूल.',
      aboutEyebrow: 'परिचय',
      aboutLine1: 'SkoleToon’s खेल, स्टाइल और विजुअल कल्चर के संगम पर पीसेज़ बनाता है.',
      aboutLine2:
        'यह स्टोर जरूरी पीस, दमदार सिल्हूट और एक्सक्लूसिव ड्रॉप्स को एक ही चुने हुए स्पेस में लाता है.',
      seePiece: 'आइटम देखें',
      products: 'उत्पाद',
      loaded: '{{count}} लोड हुए',
      reset: 'रीसेट',
      emptyTitle: 'अभी कुछ नहीं है',
      emptyText: 'आपके चयन से कोई उत्पाद मेल नहीं खाता. दूसरा कलेक्शन देखें या जल्द लौटें.',
      kidsUnavailable: 'बच्चों के उत्पाद उपलब्ध नहीं',
      kidsUnavailableText:
        'बच्चों के लिए ये उत्पाद अभी उपलब्ध नहीं हैं. दूसरी श्रेणी देखें या जल्द लौटें.',
      allDisplayed: 'सभी उत्पाद दिखाए गए हैं',
      allCollections: 'सभी कलेक्शन',
      allCategories: 'सभी श्रेणियां',
      filtersAria: 'उत्पाद फिल्टर',
      collectionsTitle: 'हमारे कलेक्शन',
      categoriesTitle: 'श्रेणियां',
      searchPlaceholder: 'खोजें',
      searchAria: 'उत्पाद खोजें',
      filterCollectionAria: 'कलेक्शन से फिल्टर करें',
      removeFavorite: 'पसंदीदा से हटाएं',
      addFavorite: 'पसंदीदा में जोड़ें',
      sale: 'सेल',
      soldOut: 'बिक चुका',
      outOfStock: 'स्टॉक में नहीं',
      adding: 'जोड़ा जा रहा है...',
      added: 'जुड़ गया',
      chooseOption: 'विकल्प चुनें',
      addToCart: 'कार्ट में जोड़ें',
      add: 'जोड़ें',
      iconic: {
        arBadge: 'AR ट्राय-ऑन उपलब्ध',
        iconicBadge: 'आइकॉनिक कलेक्शन',
        titleLine1: 'On the road',
        titleLine2: 'to success',
        brand: "— SkoleToon's",
        description:
          'मोटरस्पोर्ट और performance से प्रेरित signature collection. Jerseys, reversible jackets और checker accessories.',
        cta: 'कलेक्शन देखें',
      },
    },
    piece: {
      collection: 'कलेक्शन',
      detail: 'उत्पाद विवरण',
      less: 'कम',
      more: 'और',
      readLess: 'कम पढ़ें',
      readMore: 'और पढ़ें',
      price: 'कीमत',
      inStock: 'स्टॉक में',
      outOfStock: 'स्टॉक में नहीं',
      size: 'साइज',
      quantity: 'मात्रा',
      addToCart: 'कार्ट में जोड़ें',
      adding: 'जोड़ा जा रहा है...',
      chooseOption: 'विकल्प चुनें',
      failedAdd: 'कार्ट में जोड़ना विफल रहा',
      saved: 'सहेजा गया',
      save: 'पसंदीदा में सहेजें',
      serviceUnavailable: 'सेवा उपलब्ध नहीं',
      serviceUnavailableText: 'यह सेवा आपके क्षेत्र में उपलब्ध नहीं है.',
      back: 'वापस',
      previous: 'पिछला',
      next: 'अगला',
    },
    music: {
      pageTitleA: 'एक ट्रैक,',
      pageTitleB: 'एक universe',
      pageDescription: 'They See Us Now सिर्फ गाना नहीं है. यह एक आंदोलन की ऊर्जा है.',
      comingSoonKicker: 'Skoleom Music',
      comingSoonTitle: 'जल्द आ रहा है',
      comingSoonDescription:
        'म्यूजिक पेज जल्द आ रहा है. रिलीज से पहले हम अभी अनुभव को अंतिम रूप दे रहे हैं.',
    },
    news: {
      badge: 'Skoletoon x Skoleom न्यूज़रूम',
      titleA: 'समाचार',
      titleB: "SkoleToon's & Skoleom",
      description:
        'Skoletoon और Skoleom ecosystem के उत्पाद, साझेदारी और विस्तार की आधिकारिक घोषणाएं.',
      readArticle: 'लेख पढ़ें',
      stayInformed: 'अपडेट रहें',
      subscribe: 'सब्सक्राइब करें',
      nextAnnouncement: 'अगली घोषणा',
      shopCta: 'दुकान देखें',
      newsCta: 'समाचार देखें',
      worldNews: 'दुनिया की खबरें',
      todayHighlights: 'आज की खास बातें आपके लिए',
      lifestyle: 'लाइफस्टाइल',
      newsletterBody: 'Skoleom अपडेट, नए उत्पाद और साझेदारी घोषणाएं पाएं.',
      openFormCta: 'सब्सक्राइब करने के लिए क्लिक करें',
      validating: 'जांच हो रही है...',
    },
    search: {
      item: 'आइटम',
      collection: 'कलेक्शन',
      shop: 'दुकान',
      brand: 'ब्रांड',
      category: 'श्रेणी',
      dialog: 'खोज',
      placeholder: 'आइटम, ब्रांड, श्रेणी खोजें...',
      clear: 'साफ करें',
      noResultsFor: 'कोई परिणाम नहीं',
      featured: 'फीचर्ड',
      searchingShop: 'दुकान खोजी जा रही है...',
      seeAllResults: '"{{query}}" के सभी परिणाम देखें',
    },
  },
  zh: {
    common: { search: '搜索', play: '播放', pause: '暂停', paused: '已暂停' },
    home: {
      collectionsTitle: '系列',
      allSee: '查看全部',
      shopLabel: '商店',
      newProductsTitle: '新品',
      shopCta: '查看商店',
      newsVideoTitle: "SkoleToon's 新闻",
      newsPageAria: '查看新闻',
      newsHeroTitle: '新闻',
      newsHeroMeta: '新闻 / 发售 / 文化',
      newsArticleCta: '查看文章',
      nowPlaying: '正在播放',
      musiqueDescription: "一首歌，一种氛围。完整的 SkoleToon's 宇宙。",
      musicVisualAlt: "SkoleToon's 音乐视觉",
      musicVisualBadge: '视觉 / 照片',
      liveSpectrum: '实时音频可视化',
      seekTrack: '定位音轨',
      restartTrack: '重新播放',
      downloadTrack: '下载音轨',
      presentedBy: 'Skoleom 呈现',
      downloadExtension: '下载扩展',
      playVideo: '播放视频',
      carouselPrev: '上一个系列',
      carouselNext: '下一个系列',
      heroSlides: {
        'build-different': { collection: 'Build Different', season: '全新系列' },
        'a-modeste': { collection: 'A.Modeste', season: '全新系列' },
        'on-the-road': { collection: 'On The Road', season: '夏季编辑' },
        'maillots-sports': { collection: 'Bloc 90', season: '限量发售' },
        ontario: { collection: 'Ontario', season: '全新系列' },
      },
    },
    stores: {
      badge: '商店',
      titleA: '商',
      titleB: '店',
      featured: '精选',
      description: '探索运动造型与系列：球衣、运动套装、配饰和独家胶囊系列。',
      aboutEyebrow: '关于',
      aboutLine1: 'SkoleToon’s 在运动、风格与视觉文化的交汇处打造单品。',
      aboutLine2: '商店汇集必备款、个性廓形与独家发售，在同一精选空间呈现。',
      seePiece: '查看单品',
      products: '产品',
      loaded: '已加载 {{count}}',
      reset: '重置',
      emptyTitle: '这里暂时没有内容',
      emptyText: '没有产品匹配当前选择。请探索其他系列或稍后再来。',
      kidsUnavailable: '儿童产品暂不可用',
      kidsUnavailableText: '这些产品目前不适用于儿童。请探索其他分类或稍后再来。',
      allDisplayed: '所有产品已显示',
      allCollections: '全部系列',
      allCategories: '全部分类',
      filtersAria: '产品筛选',
      collectionsTitle: '我们的系列',
      categoriesTitle: '分类',
      searchPlaceholder: '搜索',
      searchAria: '搜索产品',
      filterCollectionAria: '按系列筛选',
      removeFavorite: '从收藏移除',
      addFavorite: '加入收藏',
      sale: '促销',
      soldOut: '售罄',
      outOfStock: '缺货',
      adding: '添加中...',
      added: '已添加',
      chooseOption: '选择选项',
      addToCart: '加入购物车',
      add: '添加',
      iconic: {
        arBadge: '支持 AR 试穿',
        iconicBadge: '标志性系列',
        titleLine1: 'On the road',
        titleLine2: 'to success',
        brand: "— SkoleToon's",
        description: '受赛车运动与性能启发的标志性系列。球衣、双面夹克与 checker 配饰。',
        cta: '探索系列',
      },
    },
    piece: {
      collection: '系列',
      detail: '产品详情',
      less: '收起',
      more: '更多',
      readLess: '收起',
      readMore: '阅读更多',
      price: '价格',
      inStock: '有库存',
      outOfStock: '缺货',
      size: '尺码',
      quantity: '数量',
      addToCart: '加入购物车',
      adding: '添加中...',
      chooseOption: '选择选项',
      failedAdd: '加入购物车失败',
      saved: '已保存',
      save: '保存到收藏',
      serviceUnavailable: '服务不可用',
      serviceUnavailableText: '此服务在你的地区不可用。',
      back: '返回',
      previous: '上一个',
      next: '下一个',
    },
    music: {
      pageTitleA: '一首歌，',
      pageTitleB: '一个宇宙',
      pageDescription: 'They See Us Now 不只是一首歌。它是一场运动的能量。',
      comingSoonKicker: 'Skoleom Music',
      comingSoonTitle: '即将上线',
      comingSoonDescription: '音乐页面即将上线。我们正在发布前完善体验。',
    },
    news: {
      badge: 'Skoletoon x Skoleom 新闻室',
      titleA: '新闻',
      titleB: "SkoleToon's 与 Skoleom",
      description: '关于 Skoletoon 与 Skoleom 生态的产品、合作和发布的官方公告。',
      readArticle: '阅读文章',
      stayInformed: '保持关注',
      subscribe: '订阅',
      nextAnnouncement: '下一条公告',
      shopCta: '查看商店',
      newsCta: '查看新闻',
      worldNews: '全球新闻',
      todayHighlights: '今日亮点，触手可及',
      lifestyle: '生活方式',
      newsletterBody: '获取 Skoleom 更新、产品发布和合作公告。',
      openFormCta: '点击订阅',
      validating: '检查中...',
    },
    search: {
      item: '单品',
      collection: '系列',
      shop: '商店',
      brand: '品牌',
      category: '分类',
      dialog: '搜索',
      placeholder: '搜索单品、品牌、分类...',
      clear: '清除',
      noResultsFor: '没有结果：',
      featured: '精选',
      searchingShop: '正在搜索商店...',
      seeAllResults: '查看“{{query}}”的全部结果',
    },
  },
  id: {
    common: { search: 'Cari', play: 'Putar', pause: 'Jeda', paused: 'Dijeda' },
    home: {
      collectionsTitle: 'Koleksi',
      allSee: 'Lihat semua',
      shopLabel: 'Toko',
      newProductsTitle: 'Produk baru',
      shopCta: 'Lihat toko',
      newsVideoTitle: "Berita SkoleToon's",
      newsPageAria: 'Lihat berita',
      newsHeroTitle: 'Berita',
      newsHeroMeta: 'Berita / Drop / Budaya',
      newsArticleCta: 'Lihat artikel',
      nowPlaying: 'Sedang diputar',
      musiqueDescription: "Satu lagu, satu vibe. Seluruh universe SkoleToon's.",
      musicVisualAlt: "Visual musik SkoleToon's",
      musicVisualBadge: 'Visual / Foto',
      liveSpectrum: 'Visualisasi audio langsung',
      seekTrack: 'Jelajahi lagu',
      restartTrack: 'Putar ulang',
      downloadTrack: 'Unduh lagu',
      presentedBy: 'Skoleom mempersembahkan',
      downloadExtension: 'Unduh ekstensi',
      playVideo: 'Putar video',
      carouselPrev: 'Koleksi sebelumnya',
      carouselNext: 'Koleksi berikutnya',
      heroSlides: {
        'build-different': { collection: 'Build Different', season: 'Koleksi baru' },
        'a-modeste': { collection: 'A.Modeste', season: 'Koleksi baru' },
        'on-the-road': { collection: 'On The Road', season: 'Edisi musim panas' },
        'maillots-sports': { collection: 'Bloc 90', season: 'Drop terbatas' },
        ontario: { collection: 'Ontario', season: 'Koleksi baru' },
      },
    },
    stores: {
      badge: 'Toko',
      titleA: 'Toko',
      titleB: 'Kami',
      featured: 'Unggulan',
      description:
        'Temukan outfit dan koleksi olahraga: jersey, tracksuit, aksesori, dan kapsul eksklusif.',
      aboutEyebrow: 'Tentang',
      aboutLine1: 'SkoleToon’s menghadirkan piece di persimpangan sport, style, dan budaya visual.',
      aboutLine2:
        'Toko ini menggabungkan essential, siluet tegas, dan drop eksklusif dalam satu ruang kurasi.',
      seePiece: 'Lihat item',
      products: 'Produk',
      loaded: '{{count}} dimuat',
      reset: 'Reset',
      emptyTitle: 'Belum ada apa-apa',
      emptyText:
        'Tidak ada produk yang cocok dengan pilihanmu. Jelajahi koleksi lain atau kembali lagi nanti.',
      kidsUnavailable: 'Produk anak belum tersedia',
      kidsUnavailableText:
        'Produk ini belum tersedia untuk anak-anak. Jelajahi kategori lain atau kembali lagi nanti.',
      allDisplayed: 'Semua produk ditampilkan',
      allCollections: 'Semua koleksi',
      allCategories: 'Semua kategori',
      filtersAria: 'Filter produk',
      collectionsTitle: 'Koleksi kami',
      categoriesTitle: 'Kategori',
      searchPlaceholder: 'Cari',
      searchAria: 'Cari produk',
      filterCollectionAria: 'Filter berdasarkan koleksi',
      removeFavorite: 'Hapus dari favorit',
      addFavorite: 'Tambah ke favorit',
      sale: 'Diskon',
      soldOut: 'Habis',
      outOfStock: 'Stok habis',
      adding: 'Menambahkan...',
      added: 'Ditambahkan',
      chooseOption: 'Pilih opsi',
      addToCart: 'Tambah ke keranjang',
      add: 'Tambah',
      iconic: {
        arBadge: 'Coba AR tersedia',
        iconicBadge: 'Koleksi ikonik',
        titleLine1: 'On the road',
        titleLine2: 'to success',
        brand: "— SkoleToon's",
        description:
          'Koleksi signature yang terinspirasi motorsport dan performa. Jersey, jaket reversible, dan aksesori checker.',
        cta: 'Jelajahi koleksi',
      },
    },
    piece: {
      collection: 'Koleksi',
      detail: 'Detail produk',
      less: 'Lebih sedikit',
      more: 'Lainnya',
      readLess: 'Baca lebih sedikit',
      readMore: 'Baca selengkapnya',
      price: 'Harga',
      inStock: 'Tersedia',
      outOfStock: 'Stok habis',
      size: 'Ukuran',
      quantity: 'Jumlah',
      addToCart: 'Tambah ke keranjang',
      adding: 'Menambahkan...',
      chooseOption: 'Pilih opsi',
      failedAdd: 'Gagal menambahkan ke keranjang',
      saved: 'Tersimpan',
      save: 'Simpan ke favorit',
      serviceUnavailable: 'Layanan tidak tersedia',
      serviceUnavailableText: 'Layanan ini tidak tersedia di wilayahmu.',
      back: 'Kembali',
      previous: 'Sebelumnya',
      next: 'Berikutnya',
    },
    music: {
      pageTitleA: 'Satu lagu,',
      pageTitleB: 'satu universe',
      pageDescription: 'They See Us Now bukan sekadar lagu. Ini energi sebuah gerakan.',
      comingSoonKicker: 'Skoleom Music',
      comingSoonTitle: 'Segera hadir',
      comingSoonDescription:
        'Halaman musik akan segera hadir. Kami sedang menyempurnakan pengalaman sebelum dirilis.',
    },
    news: {
      badge: 'Ruang berita Skoletoon x Skoleom',
      titleA: 'Berita',
      titleB: "SkoleToon's & Skoleom",
      description:
        'Semua pengumuman resmi tentang produk, kemitraan, dan peluncuran ekosistem Skoletoon dan Skoleom.',
      readArticle: 'Baca artikel',
      stayInformed: 'Tetap update',
      subscribe: 'Berlangganan',
      nextAnnouncement: 'Pengumuman berikutnya',
      shopCta: 'Lihat toko',
      newsCta: 'Lihat berita',
      worldNews: 'Berita dunia',
      todayHighlights: 'Sorotan hari ini di ujung jarimu',
      lifestyle: 'Gaya hidup',
      newsletterBody: 'Dapatkan update Skoleom, rilis produk, dan pengumuman kemitraan.',
      openFormCta: 'Klik di sini untuk berlangganan',
      validating: 'Memeriksa...',
    },
    search: {
      item: 'Item',
      collection: 'Koleksi',
      shop: 'Toko',
      brand: 'Brand',
      category: 'Kategori',
      dialog: 'Cari',
      placeholder: 'Cari item, brand, kategori...',
      clear: 'Hapus',
      noResultsFor: 'Tidak ada hasil untuk',
      featured: 'Unggulan',
      searchingShop: 'Mencari toko...',
      seeAllResults: 'Lihat semua hasil untuk "{{query}}"',
    },
  },
  ru: {
    common: { search: 'Поиск', play: 'Играть', pause: 'Пауза', paused: 'На паузе' },
    home: {
      collectionsTitle: 'Коллекции',
      allSee: 'Смотреть все',
      shopLabel: 'Магазин',
      newProductsTitle: 'Новинки',
      shopCta: 'Открыть магазин',
      newsVideoTitle: "Новости SkoleToon's",
      newsPageAria: 'Смотреть новости',
      newsHeroTitle: 'Новости',
      newsHeroMeta: 'Новости / Дропы / Культура',
      newsArticleCta: 'Открыть статью',
      nowPlaying: 'Сейчас играет',
      musiqueDescription: "Один трек, один вайб. Вся вселенная SkoleToon's.",
      musicVisualAlt: "Музыкальный визуал SkoleToon's",
      musicVisualBadge: 'Визуал / Фото',
      liveSpectrum: 'Живая аудиовизуализация',
      seekTrack: 'Перемотать трек',
      restartTrack: 'Повторить',
      downloadTrack: 'Скачать трек',
      presentedBy: 'Skoleom представляет',
      downloadExtension: 'Скачать расширение',
      playVideo: 'Смотреть видео',
      carouselPrev: 'Предыдущая коллекция',
      carouselNext: 'Следующая коллекция',
      heroSlides: {
        'build-different': { collection: 'Build Different', season: 'Новая коллекция' },
        'a-modeste': { collection: 'A.Modeste', season: 'Новая коллекция' },
        'on-the-road': { collection: 'On The Road', season: 'Летний выпуск' },
        'maillots-sports': { collection: 'Bloc 90', season: 'Лимитированный дроп' },
        ontario: { collection: 'Ontario', season: 'Новая коллекция' },
      },
    },
    stores: {
      badge: 'Магазин',
      titleA: 'Бутик',
      titleB: 'SkoleToon’s',
      featured: 'В фокусе',
      description:
        'Откройте спортивные образы и коллекции: джерси, костюмы, аксессуары и эксклюзивные капсулы.',
      aboutEyebrow: 'О коллекции',
      aboutLine1: 'SkoleToon’s создает вещи на пересечении спорта, стиля и визуальной культуры.',
      aboutLine2:
        'Магазин объединяет базовые вещи, выразительные силуэты и эксклюзивные дропы в одном тщательно отобранном пространстве.',
      seePiece: 'Смотреть вещь',
      products: 'Товары',
      loaded: 'Загружено: {{count}}',
      reset: 'Сбросить',
      emptyTitle: 'Пока ничего нет',
      emptyText: 'Нет товаров под ваш выбор. Посмотрите другую коллекцию или вернитесь позже.',
      kidsUnavailable: 'Детские товары недоступны',
      kidsUnavailableText:
        'Эти товары сейчас недоступны для детей. Выберите другую категорию или вернитесь позже.',
      allDisplayed: 'Все товары показаны',
      allCollections: 'Все коллекции',
      allCategories: 'Все категории',
      filtersAria: 'Фильтры товаров',
      collectionsTitle: 'Наши коллекции',
      categoriesTitle: 'Категории',
      searchPlaceholder: 'Поиск',
      searchAria: 'Искать товар',
      filterCollectionAria: 'Фильтр по коллекции',
      removeFavorite: 'Удалить из избранного',
      addFavorite: 'Добавить в избранное',
      sale: 'Скидка',
      soldOut: 'Распродано',
      outOfStock: 'Нет в наличии',
      adding: 'Добавление...',
      added: 'Добавлено',
      chooseOption: 'Выберите опцию',
      addToCart: 'В корзину',
      add: 'Добавить',
      iconic: {
        arBadge: 'AR-примерка доступна',
        iconicBadge: 'Иконическая коллекция',
        titleLine1: 'On the road',
        titleLine2: 'to success',
        brand: "— SkoleToon's",
        description:
          'Фирменная коллекция, вдохновлённая автоспортом и скоростью. Джерси, двусторонние куртки и аксессуары checker.',
        cta: 'Открыть коллекцию',
      },
    },
    piece: {
      collection: 'Коллекция',
      detail: 'Детали товара',
      less: 'Меньше',
      more: 'Больше',
      readLess: 'Свернуть',
      readMore: 'Читать дальше',
      price: 'Цена',
      inStock: 'В наличии',
      outOfStock: 'Нет в наличии',
      size: 'Размер',
      quantity: 'Кол-во',
      addToCart: 'В корзину',
      adding: 'Добавление...',
      chooseOption: 'Выберите опцию',
      failedAdd: 'Не удалось добавить в корзину',
      saved: 'Сохранено',
      save: 'Сохранить в избранное',
      serviceUnavailable: 'Сервис недоступен',
      serviceUnavailableText: 'Этот сервис недоступен в вашем регионе.',
      back: 'Назад',
      previous: 'Назад',
      next: 'Далее',
    },
    music: {
      pageTitleA: 'Один трек,',
      pageTitleB: 'одна вселенная',
      pageDescription: 'They See Us Now — это не просто песня. Это энергия движения.',
      comingSoonKicker: 'Skoleom Music',
      comingSoonTitle: 'Скоро',
      comingSoonDescription:
        'Музыкальная страница скоро появится. Мы завершаем последние штрихи перед запуском.',
    },
    news: {
      badge: 'Новости Skoletoon x Skoleom',
      titleA: 'Новости',
      titleB: "SkoleToon's & Skoleom",
      description:
        'Все официальные объявления о продукте, партнёрствах и развитии экосистемы Skoletoon и Skoleom.',
      readArticle: 'Читать статью',
      stayInformed: 'Будьте в курсе',
      subscribe: 'Подписаться',
      nextAnnouncement: 'Следующее объявление',
      shopCta: 'Открыть магазин',
      newsCta: 'Смотреть новости',
      worldNews: 'Мировые новости',
      todayHighlights: 'Главное сегодня у вас под рукой',
      lifestyle: 'Лайфстайл',
      newsletterBody: 'Получайте обновления Skoleom, релизы продуктов и объявления о партнёрствах.',
      openFormCta: 'Нажмите, чтобы подписаться',
      validating: 'Проверка...',
    },
    search: {
      item: 'Вещь',
      collection: 'Коллекция',
      shop: 'Магазин',
      brand: 'Бренд',
      category: 'Категория',
      dialog: 'Поиск',
      placeholder: 'Искать вещь, бренд, категорию...',
      clear: 'Очистить',
      noResultsFor: 'Нет результатов для',
      featured: 'Рекомендуемое',
      searchingShop: 'Поиск в магазине...',
      seeAllResults: 'Смотреть все результаты для "{{query}}"',
    },
  },
  sw: {
    common: { search: 'Tafuta', play: 'Cheza', pause: 'Sitisha', paused: 'Imesitishwa' },
    home: {
      collectionsTitle: 'Mikusanyo',
      allSee: 'Tazama yote',
      shopLabel: 'Duka',
      newProductsTitle: 'Bidhaa mpya',
      shopCta: 'Tazama duka',
      newsVideoTitle: "Habari za SkoleToon's",
      newsPageAria: 'Tazama habari',
      newsHeroTitle: 'Habari',
      newsHeroMeta: 'Habari / Drops / Utamaduni',
      newsArticleCta: 'Tazama makala',
      nowPlaying: 'Inacheza sasa',
      musiqueDescription: "Wimbo mmoja, vibe moja. Ulimwengu wote wa SkoleToon's.",
      musicVisualAlt: "Picha ya muziki ya SkoleToon's",
      musicVisualBadge: 'Picha / Visual',
      liveSpectrum: 'Mwonekano wa sauti moja kwa moja',
      seekTrack: 'Sogeza wimbo',
      restartTrack: 'Cheza tena',
      downloadTrack: 'Pakua wimbo',
      presentedBy: 'Skoleom inawasilisha',
      downloadExtension: 'Pakua kiendelezi',
      playVideo: 'Cheza video',
      carouselPrev: 'Mkusanyo uliopita',
      carouselNext: 'Mkusanyo unaofuata',
      heroSlides: {
        'build-different': { collection: 'Build Different', season: 'Mkusanyo mpya' },
        'a-modeste': { collection: 'A.Modeste', season: 'Mkusanyo mpya' },
        'on-the-road': { collection: 'On The Road', season: 'Toleo la kiangazi' },
        'maillots-sports': { collection: 'Bloc 90', season: 'Drop maalum' },
        ontario: { collection: 'Ontario', season: 'Mkusanyo mpya' },
      },
    },
    stores: {
      badge: 'Duka',
      titleA: 'Duka',
      titleB: 'Letu',
      featured: 'Vilivyoangaziwa',
      description:
        'Gundua mavazi na mikusanyo ya michezo: jezi, tracksuit, vifaa na capsules maalum.',
      aboutEyebrow: 'Kuhusu',
      aboutLine1:
        'SkoleToon’s huunda vipande katika makutano ya michezo, mtindo, na utamaduni wa kuona.',
      aboutLine2:
        'Duka linaunganisha essentials, silhouettes zenye msimamo, na drops maalum katika nafasi moja iliyochaguliwa kwa umakini.',
      seePiece: 'Tazama kipande',
      products: 'Bidhaa',
      loaded: '{{count}} zimepakiwa',
      reset: 'Weka upya',
      emptyTitle: 'Bado hakuna hapa',
      emptyText:
        'Hakuna bidhaa inayolingana na chaguo lako. Chunguza mkusanyo mwingine au rudi baadaye.',
      kidsUnavailable: 'Bidhaa za watoto hazipatikani',
      kidsUnavailableText:
        'Bidhaa hizi hazipatikani kwa watoto kwa sasa. Chunguza aina nyingine au rudi baadaye.',
      allDisplayed: 'Bidhaa zote zimeonyeshwa',
      allCollections: 'Mikusanyo yote',
      allCategories: 'Aina zote',
      filtersAria: 'Vichujio vya bidhaa',
      collectionsTitle: 'Mikusanyo yetu',
      categoriesTitle: 'Aina',
      searchPlaceholder: 'Tafuta',
      searchAria: 'Tafuta bidhaa',
      filterCollectionAria: 'Chuja kwa mkusanyo',
      removeFavorite: 'Ondoa kwenye vipendwa',
      addFavorite: 'Ongeza kwenye vipendwa',
      sale: 'Punguzo',
      soldOut: 'Imeisha',
      outOfStock: 'Haipo stoki',
      adding: 'Inaongezwa...',
      added: 'Imeongezwa',
      chooseOption: 'Chagua chaguo',
      addToCart: 'Ongeza kwenye kikapu',
      add: 'Ongeza',
      iconic: {
        arBadge: 'Jaribio la AR linapatikana',
        iconicBadge: 'Mkusanyo wa kipekee',
        titleLine1: 'On the road',
        titleLine2: 'to success',
        brand: "— SkoleToon's",
        description:
          'Mkusanyo wa signature uliovutiwa na motorsport na performance. Jezi, jaketi za reversible na accessories za checker.',
        cta: 'Gundua mkusanyo',
      },
    },
    piece: {
      collection: 'Mkusanyo',
      detail: 'Maelezo ya bidhaa',
      less: 'Kidogo',
      more: 'Zaidi',
      readLess: 'Soma kidogo',
      readMore: 'Soma zaidi',
      price: 'Bei',
      inStock: 'Ipo stoki',
      outOfStock: 'Haipo stoki',
      size: 'Saizi',
      quantity: 'Idadi',
      addToCart: 'Ongeza kwenye kikapu',
      adding: 'Inaongezwa...',
      chooseOption: 'Chagua chaguo',
      failedAdd: 'Imeshindikana kuongeza kwenye kikapu',
      saved: 'Imehifadhiwa',
      save: 'Hifadhi kwenye vipendwa',
      serviceUnavailable: 'Huduma haipatikani',
      serviceUnavailableText: 'Huduma hii haipatikani katika eneo lako.',
      back: 'Rudi',
      previous: 'Iliyotangulia',
      next: 'Inayofuata',
    },
    music: {
      pageTitleA: 'Wimbo mmoja,',
      pageTitleB: 'ulimwengu mmoja',
      pageDescription: 'They See Us Now si wimbo tu. Ni nguvu ya harakati.',
      comingSoonKicker: 'Skoleom Music',
      comingSoonTitle: 'Inakuja hivi karibuni',
      comingSoonDescription:
        'Ukurasa wa muziki unakuja hivi karibuni. Bado tunakamilisha uzoefu kabla ya kuutoa.',
    },
    news: {
      badge: 'Chumba cha habari Skoletoon x Skoleom',
      titleA: 'Habari',
      titleB: "SkoleToon's & Skoleom",
      description:
        'Matangazo rasmi kuhusu bidhaa, ushirikiano na uzinduzi wa mfumo wa Skoletoon na Skoleom.',
      readArticle: 'Soma makala',
      stayInformed: 'Endelea kupata taarifa',
      subscribe: 'Jisajili',
      nextAnnouncement: 'Tangazo lijalo',
      shopCta: 'Tazama duka',
      newsCta: 'Tazama habari',
      worldNews: 'Habari za dunia',
      todayHighlights: 'Muhimu za leo ziko karibu nawe',
      lifestyle: 'Mtindo wa maisha',
      newsletterBody: 'Pata masasisho ya Skoleom, bidhaa mpya na matangazo ya ushirikiano.',
      openFormCta: 'Bofya hapa kujisajili',
      validating: 'Inakagua...',
    },
    search: {
      item: 'Kipande',
      collection: 'Mkusanyo',
      shop: 'Duka',
      brand: 'Chapa',
      category: 'Aina',
      dialog: 'Tafuta',
      placeholder: 'Tafuta kipande, chapa, aina...',
      clear: 'Futa',
      noResultsFor: 'Hakuna matokeo kwa',
      featured: 'Vilivyoangaziwa',
      searchingShop: 'Inatafuta dukani...',
      seeAllResults: 'Tazama matokeo yote ya "{{query}}"',
    },
  },
};

const fallbackByLanguage: Record<LanguageCode, LanguageCode> = {
  en: 'en',
  fr: 'fr',
  es: 'es',
  ar: 'en',
  pt: 'en',
  hi: 'en',
  zh: 'en',
  id: 'en',
  ru: 'en',
  sw: 'en',
};

function mergeResource(base: LocaleResource, next: LocaleResource): LocaleResource {
  const out: LocaleResource = { ...base };
  Object.entries(next).forEach(([key, value]) => {
    const current = out[key];
    if (
      value &&
      current &&
      typeof value === 'object' &&
      typeof current === 'object' &&
      !Array.isArray(value) &&
      !Array.isArray(current)
    ) {
      out[key] = mergeResource(current as LocaleResource, value as LocaleResource);
    } else {
      out[key] = value;
    }
  });
  return out;
}

Object.entries(languageCopies).forEach(([language, resource]) => {
  skoletoonsResources[language as LanguageCode] = mergeResource(
    skoletoonsResources[fallbackByLanguage[language as LanguageCode]],
    resource,
  );
});

export default skoletoonsResources;
